<template>
  <div class="expenses-container">
    <!-- Header -->
    <div class="page-header">
      <div class="header-left">
        <h1 class="page-title">
          <i class="fas fa-receipt"></i>
          Business Expenses
        </h1>
        <p class="page-subtitle">
          Owner-paid expenses &amp; LLC reimbursement trail (tax/audit) — per company books
        </p>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="openAddModal()">
          <i class="fas fa-plus"></i>
          Add Expense
        </button>
        <button
          class="btn btn-secondary"
          :disabled="syncingGcp || selectedLegalEntity !== 'PRAETORIAN'"
          @click="syncGCP()"
          :title="selectedLegalEntity === 'PRAETORIAN'
            ? 'Pull GCP Cost table charges for Praetorian issuer projects'
            : 'GCP auto-sync is Praetorian-only until dedicated projects exist — add manual rows'"
        >
          <i class="fab fa-google" :class="{ 'fa-spin': syncingGcp }"></i>
          Sync GCP
        </button>
        <button
          class="btn btn-secondary"
          :disabled="syncing"
          @click="syncAWS()"
          title="Pull live AWS charges for this month"
        >
          <i class="fab fa-aws" :class="{ 'fa-spin': syncing }"></i>
          Sync AWS
        </button>
        <button
          class="btn btn-secondary"
          @click="showCursorHelper = !showCursorHelper"
          title="Add Cursor subscription and usage charges"
        >
          <i class="fas fa-terminal"></i>
          Add Cursor
        </button>
        <button
          class="btn btn-secondary"
          :disabled="seeding"
          @click="seedRecurring()"
          title="Copy recurring subscriptions (Cursor, GitHub…) into this month"
        >
          <i class="fas fa-redo" :class="{ 'fa-spin': seeding }"></i>
          Seed Recurring
        </button>
        <button
          class="btn btn-primary"
          :disabled="downloadingPDF || !summary || summary.expenses.length === 0"
          @click="downloadReport()"
        >
          <i class="fas fa-file-pdf" :class="{ 'fa-spin': downloadingPDF }"></i>
          Download Report
        </button>
      </div>
    </div>

    <!-- Entity switcher -->
    <div class="entity-tabs">
      <button
        v-for="ent in legalEntities"
        :key="ent.value"
        class="entity-tab"
        :class="{ 'entity-tab--active': selectedLegalEntity === ent.value }"
        @click="switchEntity(ent.value)"
      >
        {{ ent.label }}
      </button>
    </div>

    <!-- Month Picker -->
    <div class="month-picker-bar">
      <button class="month-nav-btn" @click="prevMonth()">
        <i class="fas fa-chevron-left"></i>
      </button>
      <div class="month-display">{{ monthLabel }}</div>
      <button class="month-nav-btn" @click="nextMonth()" :disabled="isCurrentMonth">
        <i class="fas fa-chevron-right"></i>
      </button>
      <span class="month-hint" v-if="isCurrentMonth">Current month</span>
    </div>

    <!-- Cursor Helper Panel -->
    <div class="cursor-helper" v-if="showCursorHelper">
      <div class="cursor-helper-header">
        <div class="cursor-helper-title">
          <i class="fas fa-terminal"></i>
          Add Cursor Charges
        </div>
        <button class="btn-link" @click="showCursorHelper = false">×</button>
      </div>

      <!-- Billing explainer -->
      <div class="cursor-billing-explainer">
        <div class="billing-explainer-item">
          <span class="explainer-label">Flat subscription</span>
          <span class="explainer-amount">$200/mo</span>
          <span class="explainer-note">Charged on your billing date (15th). Covers $709+ of included model usage.</span>
        </div>
        <div class="billing-explainer-sep">+</div>
        <div class="billing-explainer-item">
          <span class="explainer-label">On-demand overages</span>
          <span class="explainer-amount explainer-variable">variable</span>
          <span class="explainer-note">Billed in arrears when you exceed included usage. April was $189.05.</span>
        </div>
        <div class="billing-explainer-sep">=</div>
        <div class="billing-explainer-item billing-explainer-total">
          <span class="explainer-label">Your actual expense</span>
          <span class="explainer-amount explainer-total-amt">$200 + overages</span>
          <span class="explainer-note">The $898 "total spend" shown in Cursor is model cost value, not what you're charged.</span>
        </div>
      </div>

      <!-- CSV import -->
      <div class="cursor-csv-section">
        <div class="csv-section-title">
          <i class="fas fa-file-csv"></i>
          Import from CSV
          <span class="csv-section-hint">
            Go to
            <a href="https://cursor.com/dashboard/usage" target="_blank" rel="noopener">
              cursor.com/dashboard/usage
            </a>
            → select a month → click "Export CSV"
          </span>
        </div>
        <div class="csv-drop-zone"
             :class="{ 'csv-drop-zone--over': csvDragOver }"
             @click="$refs.csvInput.click()"
             @dragover.prevent="csvDragOver = true"
             @dragleave="csvDragOver = false"
             @drop.prevent="onCsvDrop($event)">
          <i class="fas fa-upload"></i>
          <span v-if="!csvFile">Click or drop cursor-usage-export.csv here</span>
          <span v-else class="csv-file-name">
            <i class="fas fa-check-circle" style="color:#7986cb"></i>
            {{ csvFile.name }}
          </span>
          <input ref="csvInput" type="file" accept=".csv" style="display:none" @change="onCsvSelected($event)" />
        </div>

        <!-- CSV preview -->
        <div class="csv-preview" v-if="csvPreview">
          <div class="csv-preview-row" v-for="(data, monthKey) in csvPreview" :key="monthKey">
            <div class="csv-preview-month">{{ data.month_label }}</div>
            <div class="csv-preview-amounts">
              <span class="csv-sub">Sub: $200.00</span>
              <span class="csv-included">Included: ${{ data.included_spend }}</span>
              <span class="csv-ondemand" v-if="parseFloat(data.on_demand_spend) > 0">
                On-demand: ${{ data.on_demand_spend }}
              </span>
              <span class="csv-ondemand csv-ondemand--zero" v-else>No overages</span>
              <span class="csv-total">
                <strong>Your charge: ${{ (200 + parseFloat(data.on_demand_spend)).toFixed(2) }}</strong>
              </span>
            </div>
          </div>
          <div class="csv-preview-actions">
            <button class="btn btn-secondary btn-sm" @click="csvPreview = null; csvFile = null">
              Clear
            </button>
            <button class="btn btn-primary btn-sm" :disabled="importingCsv" @click="importCsv()">
              <i class="fas fa-download" v-if="!importingCsv"></i>
              <i class="fas fa-spinner fa-spin" v-else></i>
              Import {{ Object.keys(csvPreview).length }} month(s)
            </button>
          </div>
        </div>

        <button
          v-if="csvFile && !csvPreview"
          class="btn btn-secondary btn-sm"
          :disabled="parsingCsv"
          @click="parseCsv()"
          style="margin-top: 10px;"
        >
          <i class="fas fa-search" v-if="!parsingCsv"></i>
          <i class="fas fa-spinner fa-spin" v-else></i>
          Preview CSV
        </button>
      </div>

      <!-- Manual quick-add buttons -->
      <div class="cursor-quick-btns">
        <button class="cursor-quick-btn" @click="quickAddCursor('subscription')">
          <div class="quick-btn-label">
            <i class="fas fa-plus-circle"></i>
            Add $200 Subscription (manual)
          </div>
          <div class="quick-btn-sub">Flat Ultra plan — marks as recurring</div>
        </button>
        <button class="cursor-quick-btn" @click="quickAddCursor('ondemand')">
          <div class="quick-btn-label">
            <i class="fas fa-plus-circle"></i>
            Add On-Demand Overage (manual)
          </div>
          <div class="quick-btn-sub">Enter the amount from your invoice</div>
        </button>
      </div>
    </div>

    <!-- Sync/error banner -->
    <div class="alert alert-success" v-if="syncMessage">
      <i class="fas fa-check-circle"></i>
      {{ syncMessage }}
      <button class="alert-close" @click="syncMessage = ''">×</button>
    </div>
    <div class="alert alert-danger" v-if="errorMessage">
      <i class="fas fa-exclamation-circle"></i>
      {{ errorMessage }}
      <button class="alert-close" @click="errorMessage = ''">×</button>
    </div>

    <!-- Loading -->
    <div class="loading-state" v-if="loading">
      <i class="fas fa-spinner fa-spin"></i> Loading expenses…
    </div>

    <template v-else-if="summary">
      <!-- Reimbursement Banner -->
      <div class="reimburse-banner" v-if="parseFloat(summary.pending_reimbursement) > 0">
        <div class="banner-text">
          <div class="banner-label">
            <i class="fas fa-money-bill-transfer"></i>
            Pending Reimbursement — {{ summary.month_label }}
          </div>
          <div class="banner-sub">
            Transfer this exact amount from {{ entityLabel }} to yourself
          </div>
        </div>
        <div class="banner-amount">${{ summary.pending_reimbursement }}</div>
        <button class="btn btn-success btn-sm" @click="markAllReimbursed()">
          <i class="fas fa-check"></i> Mark All Reimbursed
        </button>
      </div>

      <!-- All reimbursed banner -->
      <div class="reimbursed-banner" v-else-if="parseFloat(summary.grand_total) > 0">
        <i class="fas fa-check-circle"></i>
        All expenses for {{ summary.month_label }} have been reimbursed
        (${{ summary.reimbursed_total }})
      </div>

      <!-- Summary Cards -->
      <div class="summary-cards">
        <div
          class="summary-card"
          v-for="(data, vendor) in summary.by_vendor"
          :key="vendor"
        >
          <div class="card-icon">
            <i :class="vendorIcon(vendor)"></i>
          </div>
          <div class="card-body">
            <div class="card-value">${{ data.total }}</div>
            <div class="card-label">{{ vendor }}</div>
            <div class="card-sub">{{ data.count }} item{{ data.count !== 1 ? 's' : '' }}</div>
          </div>
        </div>
        <div class="summary-card summary-card--total">
          <div class="card-icon">
            <i class="fas fa-sigma"></i>
          </div>
          <div class="card-body">
            <div class="card-value">${{ summary.grand_total }}</div>
            <div class="card-label">Total This Month</div>
            <div class="card-sub">
              <span v-if="parseFloat(summary.pending_reimbursement) > 0" class="pending-text">
                ${{ summary.pending_reimbursement }} pending
              </span>
              <span v-else class="paid-text">Fully reimbursed</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Expense Table -->
      <div class="table-section">
        <div class="section-header">
          <div class="section-title">Itemized Expenses</div>
          <span class="item-count">{{ summary.expenses.length }} items</span>
        </div>
        <div class="expense-table-wrap">
          <table class="expense-table" v-if="summary.expenses.length > 0">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Category</th>
                <th>Description</th>
                <th>Source</th>
                <th class="right">Amount</th>
                <th>Reimbursement purpose</th>
                <th class="center">Status</th>
                <th class="center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="exp in summary.expenses"
                :key="exp.id"
                :class="{ 'row-reimbursed': exp.is_reimbursed }"
              >
                <td>
                  <strong>{{ exp.vendor }}</strong>
                  <span v-if="exp.is_recurring" class="recurring-badge" title="Recurring expense">
                    <i class="fas fa-redo-alt"></i>
                  </span>
                </td>
                <td>
                  <span class="cat-badge" :class="`cat-${exp.category}`">
                    {{ exp.category_label }}
                  </span>
                </td>
                <td class="desc-cell">
                  {{ exp.description }}
                  <span v-if="exp.notes && exp.source !== 'AUTO_AWS'" class="exp-notes">
                    {{ exp.notes }}
                  </span>
                </td>
                <td>
                  <span class="source-badge">
                    <i v-if="exp.source === 'AUTO_AWS'" class="fab fa-aws"></i>
                    <i v-else-if="exp.source === 'AUTO_GCP'" class="fab fa-google"></i>
                    <i v-else class="fas fa-pen"></i>
                    {{ sourceLabel(exp.source) }}
                  </span>
                </td>
                <td class="right amount-cell">${{ exp.amount_usd }}</td>
                <td class="purpose-cell">
                  <span v-if="exp.reimbursement_purpose">{{ exp.reimbursement_purpose }}</span>
                  <span v-else class="muted">—</span>
                </td>
                <td class="center">
                  <span v-if="exp.is_reimbursed" class="status-badge status-paid">
                    <i class="fas fa-check"></i> Paid
                  </span>
                  <span v-else class="status-badge status-pending">
                    <i class="fas fa-clock"></i> Pending
                  </span>
                </td>
                <td class="center actions-cell">
                  <button
                    v-if="!exp.is_reimbursed"
                    class="action-btn action-btn--reimburse"
                    @click="openReimburseModal(exp)"
                    title="Mark reimbursed"
                  >
                    <i class="fas fa-check-circle"></i>
                  </button>
                  <button
                    v-else
                    class="action-btn action-btn--undo"
                    @click="unreimburse(exp)"
                    title="Undo reimbursement"
                  >
                    <i class="fas fa-undo"></i>
                  </button>
                  <button
                    class="action-btn action-btn--edit"
                    @click="openEditModal(exp)"
                    title="Edit"
                  >
                    <i class="fas fa-pencil-alt"></i>
                  </button>
                  <button
                    class="action-btn action-btn--delete"
                    @click="deleteExpense(exp)"
                    title="Delete"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="totals-row">
                <td colspan="5"><strong>Total</strong></td>
                <td class="right"><strong>${{ summary.grand_total }}</strong></td>
                <td colspan="2"></td>
              </tr>
            </tfoot>
          </table>

          <div class="empty-state" v-else>
            <i class="fas fa-inbox"></i>
            <p>No expenses for {{ summary.month_label }}.</p>
            <p class="empty-hint">
              Click <strong>Sync AWS</strong> or <strong>Sync GCP</strong> (Praetorian) for infrastructure,
              or <strong>Add Expense</strong> for manual ADI/Vet Media charges.
            </p>
          </div>
        </div>
      </div>
    </template>

    <!-- YTD chart section -->
    <div class="ytd-section" v-if="ytd && ytd.months && ytd.months.length > 0">
      <div class="section-header" style="margin-top: 32px;">
        <div class="section-title">{{ currentYear }} Year-to-Date</div>
        <span class="item-count">{{ ytd.months.length }} months</span>
      </div>
      <div class="ytd-table-wrap">
        <table class="ytd-table">
          <thead>
            <tr>
              <th>Month</th>
              <th class="right">Total</th>
              <th class="right">Reimbursed</th>
              <th class="right">Pending</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="m in ytd.months"
              :key="`${m.year}-${m.month}`"
              :class="{ 'ytd-current': m.year === currentYear && m.month === currentMonth }"
              style="cursor: pointer;"
              @click="goToMonth(m.year, m.month)"
            >
              <td>
                {{ m.month_label }}
                <span v-if="m.year === currentYear && m.month === currentMonth" class="current-label">
                  ← viewing
                </span>
              </td>
              <td class="right">${{ m.total }}</td>
              <td class="right">
                <span v-if="parseFloat(m.reimbursed) > 0" class="paid-text">${{ m.reimbursed }}</span>
                <span v-else class="muted">—</span>
              </td>
              <td class="right">
                <span v-if="parseFloat(m.pending) > 0" class="pending-text">${{ m.pending }}</span>
                <span v-else class="paid-text">Paid</span>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td><strong>YTD Total</strong></td>
              <td class="right"><strong>${{ ytdTotal }}</strong></td>
              <td class="right">
                <strong class="paid-text">${{ ytdReimbursed }}</strong>
              </td>
              <td class="right">
                <strong class="pending-text">${{ ytdPending }}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- ────────────────────────────────────────────────────────────
         Add / Edit Expense Modal
    ──────────────────────────────────────────────────────────────── -->
    <div class="modal-overlay" v-if="showExpenseModal" @click.self="closeExpenseModal()">
      <div class="modal">
        <div class="modal-header">
          <h3>{{ editingExpense ? 'Edit Expense' : 'Add Business Expense' }}</h3>
          <button class="modal-close" @click="closeExpenseModal()">×</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group">
              <label>Vendor <span class="required">*</span></label>
              <input v-model="form.vendor" type="text" class="form-input" placeholder="Cursor, GitHub, AWS…" />
            </div>
            <div class="form-group">
              <label>Category</label>
              <select v-model="form.category" class="form-input">
                <option value="INFRASTRUCTURE">Infrastructure</option>
                <option value="AI_TOOLS">AI Tools</option>
                <option value="DEV_TOOLS">Developer Tools</option>
                <option value="SAAS">SaaS / Subscriptions</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
          </div>
          <div class="form-group">
            <label>Description <span class="required">*</span></label>
            <input v-model="form.description" type="text" class="form-input" placeholder="e.g. Cursor Pro subscription – May 2026" />
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Amount (USD) <span class="required">*</span></label>
              <input v-model="form.amount_usd" type="number" step="0.01" min="0" class="form-input" placeholder="0.00" />
            </div>
            <div class="form-group">
              <label>Expense Month</label>
              <input v-model="form.expense_month" type="month" class="form-input" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group form-group--checkbox">
              <label class="checkbox-label">
                <input v-model="form.is_recurring" type="checkbox" />
                Recurring (seed every month)
              </label>
            </div>
            <div class="form-group">
              <label>Receipt URL</label>
              <input v-model="form.receipt_url" type="url" class="form-input" placeholder="https://…" />
            </div>
          </div>
          <div class="form-group">
            <label>Notes</label>
            <textarea v-model="form.notes" class="form-input" rows="2" placeholder="Optional context…"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeExpenseModal()">Cancel</button>
          <button class="btn btn-primary" :disabled="saving" @click="saveExpense()">
            <i class="fas fa-save" v-if="!saving"></i>
            <i class="fas fa-spinner fa-spin" v-else></i>
            {{ editingExpense ? 'Save Changes' : 'Add Expense' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ────────────────────────────────────────────────────────────
         Reimburse Modal
    ──────────────────────────────────────────────────────────────── -->
    <div class="modal-overlay" v-if="showReimburseModal" @click.self="closeReimburseModal()">
      <div class="modal modal--sm">
        <div class="modal-header">
          <h3>Mark as Reimbursed</h3>
          <button class="modal-close" @click="closeReimburseModal()">×</button>
        </div>
        <div class="modal-body" v-if="reimbursingExpense">
          <p class="reimburse-detail">
            <strong>{{ reimbursingExpense.vendor }}</strong> — {{ reimbursingExpense.description }}
          </p>
          <div class="form-group">
            <label>Transfer Amount (USD)</label>
            <input
              v-model="reimburseForm.amount"
              type="number"
              step="0.01"
              min="0"
              class="form-input form-input--lg"
              :placeholder="reimbursingExpense.amount_usd"
            />
            <small class="form-hint">
              Original amount: ${{ reimbursingExpense.amount_usd }}
            </small>
          </div>
          <div class="form-group">
            <label>Reimbursement purpose <span class="required">*</span></label>
            <textarea
              v-model="reimburseForm.purpose"
              class="form-input"
              rows="3"
              placeholder="e.g. July 2026 AWS for ADI production hosting — owner-paid, LLC reimbursement"
            ></textarea>
            <small class="form-hint">Required for IRS/audit paper trail</small>
          </div>
          <div class="form-group">
            <label>Transfer notes (optional)</label>
            <input v-model="reimburseForm.notes" type="text" class="form-input" placeholder="e.g. transferred via Zelle May 21" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeReimburseModal()">Cancel</button>
          <button class="btn btn-success" :disabled="saving" @click="confirmReimburse()">
            <i class="fas fa-check" v-if="!saving"></i>
            <i class="fas fa-spinner fa-spin" v-else></i>
            Confirm Reimbursement
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

const LEGAL_ENTITIES = [
  { value: 'PRAETORIAN', label: 'Praetorian AI LLC' },
  { value: 'ADI', label: 'ADI' },
  { value: 'VET_MEDIA', label: 'Vet Media Group' },
];

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function defaultForm(year, month, legalEntity = 'PRAETORIAN') {
  const pad = String(month).padStart(2, '0');
  return {
    legal_entity: legalEntity,
    vendor: '',
    category: 'OTHER',
    description: '',
    amount_usd: '',
    expense_month: `${year}-${pad}`,
    source: 'MANUAL',
    receipt_url: '',
    is_recurring: false,
    notes: '',
  };
}

export default {
  name: 'BusinessExpenses',

  data() {
    const now = new Date();
    return {
      currentYear: now.getFullYear(),
      currentMonth: now.getMonth() + 1,
      selectedLegalEntity: 'PRAETORIAN',
      legalEntities: LEGAL_ENTITIES,

      summary: null,
      ytd: null,
      loading: false,
      syncing: false,
      syncingGcp: false,
      seeding: false,
      downloadingPDF: false,
      saving: false,

      syncMessage: '',
      errorMessage: '',
      showCursorHelper: false,

      // CSV import
      csvFile: null,
      csvDragOver: false,
      csvPreview: null,
      parsingCsv: false,
      importingCsv: false,

      showExpenseModal: false,
      editingExpense: null,
      form: defaultForm(now.getFullYear(), now.getMonth() + 1, 'PRAETORIAN'),

      showReimburseModal: false,
      reimbursingExpense: null,
      reimburseForm: { amount: '', purpose: '', notes: '' },
    };
  },

  computed: {
    entityLabel() {
      const ent = this.legalEntities.find(e => e.value === this.selectedLegalEntity);
      return ent?.label || this.selectedLegalEntity;
    },

    entityQueryParams() {
      return { legal_entity: this.selectedLegalEntity };
    },

    monthLabel() {
      return `${MONTHS[this.currentMonth - 1]} ${this.currentYear}`;
    },

    isCurrentMonth() {
      const now = new Date();
      return this.currentYear === now.getFullYear() && this.currentMonth === now.getMonth() + 1;
    },

    ytdTotal() {
      if (!this.ytd?.months) return '0.00';
      return this.ytd.months
        .reduce((acc, m) => acc + parseFloat(m.total || 0), 0)
        .toFixed(2);
    },

    ytdReimbursed() {
      if (!this.ytd?.months) return '0.00';
      return this.ytd.months
        .reduce((acc, m) => acc + parseFloat(m.reimbursed || 0), 0)
        .toFixed(2);
    },

    ytdPending() {
      if (!this.ytd?.months) return '0.00';
      return this.ytd.months
        .reduce((acc, m) => acc + parseFloat(m.pending || 0), 0)
        .toFixed(2);
    },
  },

  async mounted() {
    await this.loadData();
  },

  methods: {
    async switchEntity(value) {
      if (this.selectedLegalEntity === value) return;
      this.selectedLegalEntity = value;
      await this.loadData();
    },

    // ------------------------------------------------------------------
    // Data loading
    // ------------------------------------------------------------------

    async loadData() {
      this.loading = true;
      try {
        await Promise.all([this.loadSummary(), this.loadYTD()]);
      } finally {
        this.loading = false;
      }
    },

    async loadSummary() {
      try {
        const res = await api.get('/business-expenses/monthly-summary/', {
          params: {
            year: this.currentYear,
            month: this.currentMonth,
            ...this.entityQueryParams,
          },
        });
        this.summary = res.data;
      } catch (e) {
        this.errorMessage = 'Failed to load expense summary.';
      }
    },

    async loadYTD() {
      try {
        const res = await api.get('/business-expenses/ytd-summary/', {
          params: { year: this.currentYear, ...this.entityQueryParams },
        });
        this.ytd = res.data;
      } catch (e) {
        console.error('Failed to load YTD', e);
      }
    },

    // ------------------------------------------------------------------
    // Month navigation
    // ------------------------------------------------------------------

    async prevMonth() {
      if (this.currentMonth === 1) {
        this.currentYear -= 1;
        this.currentMonth = 12;
      } else {
        this.currentMonth -= 1;
      }
      await this.loadData();
    },

    async nextMonth() {
      if (this.isCurrentMonth) return;
      if (this.currentMonth === 12) {
        this.currentYear += 1;
        this.currentMonth = 1;
      } else {
        this.currentMonth += 1;
      }
      await this.loadData();
    },

    async goToMonth(year, month) {
      this.currentYear = year;
      this.currentMonth = month;
      await this.loadData();
    },

    // ------------------------------------------------------------------
    // AWS Sync
    // ------------------------------------------------------------------

    async syncAWS() {
      this.syncing = true;
      this.syncMessage = '';
      this.errorMessage = '';
      try {
        const res = await api.post('/business-expenses/sync-aws/', null, {
          params: {
            year: this.currentYear,
            month: this.currentMonth,
            ...this.entityQueryParams,
          },
        });
        const { created, updated } = res.data;
        this.syncMessage = `AWS sync complete — ${created} new, ${updated} updated entries for ${this.monthLabel}.`;
        await this.loadData();
      } catch (e) {
        this.errorMessage = 'AWS sync failed: ' + (e.response?.data?.error || e.message);
      } finally {
        this.syncing = false;
      }
    },

    async syncGCP() {
      if (this.selectedLegalEntity !== 'PRAETORIAN') {
        this.errorMessage = 'GCP auto-sync is Praetorian-only. Add GCP/AWS charges manually for ADI or Vet Media.';
        return;
      }
      this.syncingGcp = true;
      this.syncMessage = '';
      this.errorMessage = '';
      try {
        const res = await api.post('/business-expenses/sync-gcp/', null, {
          params: {
            year: this.currentYear,
            month: this.currentMonth,
            legal_entity: 'PRAETORIAN',
          },
        });
        const { created, updated } = res.data;
        this.syncMessage = `GCP sync complete — ${created} new, ${updated} updated for ${this.monthLabel}.`;
        await this.loadData();
      } catch (e) {
        this.errorMessage = 'GCP sync failed: ' + (e.response?.data?.error || e.message);
      } finally {
        this.syncingGcp = false;
      }
    },

    // ------------------------------------------------------------------
    // Cursor CSV import
    // ------------------------------------------------------------------

    onCsvSelected(event) {
      this.csvFile = event.target.files[0] || null;
      this.csvPreview = null;
    },

    onCsvDrop(event) {
      this.csvDragOver = false;
      const file = event.dataTransfer?.files?.[0];
      if (file && file.name.endsWith('.csv')) {
        this.csvFile = file;
        this.csvPreview = null;
      } else {
        this.errorMessage = 'Please drop a .csv file.';
      }
    },

    async parseCsv() {
      if (!this.csvFile) return;
      this.parsingCsv = true;
      this.errorMessage = '';
      try {
        const formData = new FormData();
        formData.append('file', this.csvFile);
        const res = await api.post('/business-expenses/import-cursor-csv/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          // preview only — don't create records yet
        });
        this.csvPreview = res.data.preview;
        if (res.data.errors?.length) {
          console.warn('CSV parse warnings:', res.data.errors);
        }
      } catch (e) {
        this.errorMessage = 'CSV parse failed: ' + (e.response?.data?.error || e.message);
      } finally {
        this.parsingCsv = false;
      }
    },

    async importCsv() {
      if (!this.csvFile || !this.csvPreview) return;
      this.importingCsv = true;
      this.errorMessage = '';
      try {
        const formData = new FormData();
        formData.append('file', this.csvFile);
        const res = await api.post('/business-expenses/import-cursor-csv/', formData, {
          params: { create: 'true', subscription_fee: '200.00', ...this.entityQueryParams },
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const { created, updated, months_processed } = res.data;
        this.syncMessage = `Cursor CSV imported — ${months_processed} month(s), ${created} created, ${updated} updated.`;
        this.csvFile = null;
        this.csvPreview = null;
        this.showCursorHelper = false;
        await this.loadData();
      } catch (e) {
        this.errorMessage = 'CSV import failed: ' + (e.response?.data?.error || e.message);
      } finally {
        this.importingCsv = false;
      }
    },

    // ------------------------------------------------------------------
    // Cursor quick-add helpers
    // ------------------------------------------------------------------

    quickAddCursor(type) {
      this.showCursorHelper = false;
      this.editingExpense = null;
      const pad = String(this.currentMonth).padStart(2, '0');
      const monthStr = `${this.currentYear}-${pad}`;
      const monthLabel = this.monthLabel;

      if (type === 'subscription') {
        this.form = {
          legal_entity: this.selectedLegalEntity,
          vendor: 'Cursor',
          category: 'AI_TOOLS',
          description: `Cursor Ultra subscription — ${monthLabel}`,
          amount_usd: '200.00',
          expense_month: monthStr,
          source: 'MANUAL',
          receipt_url: '',
          is_recurring: true,
          notes: 'Ultra plan $200/mo flat fee',
        };
      } else {
        this.form = {
          legal_entity: this.selectedLegalEntity,
          vendor: 'Cursor',
          category: 'AI_TOOLS',
          description: `Cursor on-demand usage — ${monthLabel}`,
          amount_usd: '',
          expense_month: monthStr,
          source: 'MANUAL',
          receipt_url: 'https://cursor.com/dashboard/billing',
          is_recurring: false,
          notes: 'On-demand overage — see Billing & Invoices at cursor.com/dashboard/billing',
        };
      }
      this.showExpenseModal = true;
    },

    // ------------------------------------------------------------------
    // Seed recurring
    // ------------------------------------------------------------------

    async seedRecurring() {
      this.seeding = true;
      this.syncMessage = '';
      this.errorMessage = '';
      try {
        const res = await api.post('/business-expenses/seed-recurring/', null, {
          params: {
            year: this.currentYear,
            month: this.currentMonth,
            ...this.entityQueryParams,
          },
        });
        const { seeded } = res.data;
        if (seeded > 0) {
          this.syncMessage = `Seeded ${seeded} recurring expense(s) into ${this.monthLabel}.`;
          await this.loadData();
        } else {
          this.syncMessage = 'No new recurring expenses to seed for this month.';
        }
      } catch (e) {
        this.errorMessage = 'Seed failed: ' + (e.response?.data?.error || e.message);
      } finally {
        this.seeding = false;
      }
    },

    // ------------------------------------------------------------------
    // Download PDF
    // ------------------------------------------------------------------

    async downloadReport() {
      this.downloadingPDF = true;
      try {
        const res = await api.get('/business-expenses/generate-report/', {
          params: {
            year: this.currentYear,
            month: this.currentMonth,
            ...this.entityQueryParams,
          },
          responseType: 'blob',
        });
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const entitySlug = this.selectedLegalEntity.toLowerCase().replace('_', '-');
        link.download = `Expense-Report-${entitySlug}-${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.errorMessage = 'PDF download failed: ' + (e.response?.data?.error || e.message);
      } finally {
        this.downloadingPDF = false;
      }
    },

    // ------------------------------------------------------------------
    // Add / Edit expense
    // ------------------------------------------------------------------

    openAddModal() {
      this.editingExpense = null;
      this.form = defaultForm(this.currentYear, this.currentMonth, this.selectedLegalEntity);
      this.showExpenseModal = true;
    },

    openEditModal(exp) {
      this.editingExpense = exp;
      const [y, m] = (exp.expense_month || '').split('-');
      this.form = {
        legal_entity: exp.legal_entity || this.selectedLegalEntity,
        vendor: exp.vendor,
        category: exp.category,
        description: exp.description,
        amount_usd: exp.amount_usd,
        expense_month: `${y}-${m}`,
        source: exp.source,
        receipt_url: exp.receipt_url || '',
        is_recurring: exp.is_recurring,
        notes: exp.notes || '',
      };
      this.showExpenseModal = true;
    },

    closeExpenseModal() {
      this.showExpenseModal = false;
      this.editingExpense = null;
    },

    async saveExpense() {
      if (!this.form.vendor.trim() || !this.form.description.trim() || !this.form.amount_usd) {
        this.errorMessage = 'Vendor, description, and amount are required.';
        return;
      }

      this.saving = true;
      try {
        // Convert "2026-05" to "2026-05-01"
        const expenseMonth = this.form.expense_month.length === 7
          ? `${this.form.expense_month}-01`
          : this.form.expense_month;

        const payload = { ...this.form, expense_month: expenseMonth };

        if (this.editingExpense) {
          await api.patch(`/business-expenses/${this.editingExpense.id}/`, payload);
        } else {
          await api.post('/business-expenses/', payload);
        }

        this.closeExpenseModal();
        await this.loadData();
      } catch (e) {
        this.errorMessage = 'Save failed: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message);
      } finally {
        this.saving = false;
      }
    },

    async deleteExpense(exp) {
      if (!confirm(`Delete "${exp.vendor} — ${exp.description}"?`)) return;
      try {
        await api.delete(`/business-expenses/${exp.id}/`);
        await this.loadData();
      } catch (e) {
        this.errorMessage = 'Delete failed: ' + (e.response?.data?.error || e.message);
      }
    },

    // ------------------------------------------------------------------
    // Reimburse
    // ------------------------------------------------------------------

    openReimburseModal(exp) {
      this.reimbursingExpense = exp;
      const monthLabel = this.monthLabel;
      const defaultPurpose = `${monthLabel} ${exp.vendor} — owner-paid, ${this.entityLabel} reimbursement`;
      this.reimburseForm = { amount: exp.amount_usd, purpose: defaultPurpose, notes: '' };
      this.showReimburseModal = true;
    },

    closeReimburseModal() {
      this.showReimburseModal = false;
      this.reimbursingExpense = null;
    },

    async confirmReimburse() {
      if (!this.reimburseForm.amount) {
        this.errorMessage = 'Enter the transfer amount.';
        return;
      }
      if (!this.reimburseForm.purpose?.trim()) {
        this.errorMessage = 'Reimbursement purpose is required for tax/audit trail.';
        return;
      }
      this.saving = true;
      try {
        await api.post(`/business-expenses/${this.reimbursingExpense.id}/reimburse/`, {
          reimbursed_amount: this.reimburseForm.amount,
          reimbursement_purpose: this.reimburseForm.purpose.trim(),
          notes: this.reimburseForm.notes,
        });
        this.closeReimburseModal();
        await this.loadData();
      } catch (e) {
        this.errorMessage = 'Reimburse failed: ' + (e.response?.data?.error || e.message);
      } finally {
        this.saving = false;
      }
    },

    async unreimburse(exp) {
      if (!confirm('Undo this reimbursement?')) return;
      try {
        await api.post(`/business-expenses/${exp.id}/unreimburse/`);
        await this.loadData();
      } catch (e) {
        this.errorMessage = 'Undo failed: ' + (e.response?.data?.error || e.message);
      }
    },

    async markAllReimbursed() {
      const pending = (this.summary?.expenses || []).filter(e => !e.is_reimbursed);
      if (pending.length === 0) return;

      const defaultPurpose = `${this.monthLabel} business expenses — owner-paid, ${this.entityLabel} reimbursement`;
      const purpose = window.prompt(
        `Reimbursement purpose for all ${pending.length} items (required):`,
        defaultPurpose,
      );
      if (!purpose?.trim()) {
        this.errorMessage = 'Reimbursement purpose is required.';
        return;
      }
      if (!confirm(`Mark all ${pending.length} pending expenses as reimbursed for $${this.summary.pending_reimbursement}?`)) return;

      try {
        await Promise.all(
          pending.map(exp =>
            api.post(`/business-expenses/${exp.id}/reimburse/`, {
              reimbursed_amount: exp.amount_usd,
              reimbursement_purpose: purpose.trim(),
            })
          )
        );
        await this.loadData();
        this.syncMessage = `Marked ${pending.length} expenses as reimbursed for ${this.monthLabel}.`;
      } catch (e) {
        this.errorMessage = 'Failed to mark all reimbursed: ' + (e.response?.data?.error || e.message);
      }
    },

    // ------------------------------------------------------------------
    // Helpers
    // ------------------------------------------------------------------

    sourceLabel(source) {
      if (source === 'AUTO_AWS') return 'AWS Sync';
      if (source === 'AUTO_GCP') return 'GCP Sync';
      return 'Manual';
    },

    vendorIcon(vendor) {
      const v = (vendor || '').toLowerCase();
      if (v === 'aws') return 'fab fa-aws';
      if (v === 'cursor') return 'fas fa-terminal';
      if (v === 'github') return 'fab fa-github';
      if (v.includes('google') || v.includes('gemini')) return 'fab fa-google';
      if (v.includes('openai') || v === 'chatgpt') return 'fas fa-robot';
      if (v.includes('anthropic') || v === 'claude') return 'fas fa-brain';
      return 'fas fa-receipt';
    },
  },
};
</script>

<style scoped>
.expenses-container {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

/* ── Header ── */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
}

.page-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1a1a2e;
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-title i { color: #2c3e8c; }

.page-subtitle {
  color: #666;
  font-size: 0.9rem;
  margin-top: 4px;
}

.header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

/* ── Entity tabs ── */
.entity-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.entity-tab {
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #c5cae9;
  background: #fff;
  color: #2c3e8c;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.entity-tab:hover { background: #e8eaf6; }

.entity-tab--active {
  background: #2c3e8c;
  color: #fff;
  border-color: #2c3e8c;
}

.purpose-cell {
  max-width: 220px;
  font-size: 0.82rem;
  color: #555;
}

/* ── Month Picker ── */
.month-picker-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8f9ff;
  border: 1px solid #e0e4f0;
  border-radius: 10px;
  padding: 12px 20px;
  margin-bottom: 20px;
}

.month-display {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2c3e8c;
  min-width: 160px;
  text-align: center;
}

.month-nav-btn {
  background: #fff;
  border: 1px solid #dde2f0;
  border-radius: 6px;
  width: 34px;
  height: 34px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2c3e8c;
  transition: background 0.15s;
}

.month-nav-btn:hover:not(:disabled) { background: #e8eaf6; }
.month-nav-btn:disabled { opacity: 0.4; cursor: default; }

.month-hint {
  font-size: 0.8rem;
  color: #888;
  font-style: italic;
}

/* ── Alerts ── */
.alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.9rem;
}

.alert-success { background: #e8f5e9; color: #2e7d32; border: 1px solid #a5d6a7; }
.alert-danger  { background: #fce4ec; color: #c62828; border: 1px solid #ef9a9a; }
.alert-warning { background: #fff8e1; color: #e65100; border: 1px solid #ffe082; }
.alert-warning code { background: rgba(0,0,0,0.06); padding: 1px 4px; border-radius: 3px; font-size: 0.85em; }

/* ── Cursor Helper Panel ── */
.cursor-helper {
  background: #1a1a2e;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 20px;
  color: #e0e0e0;
}

.cursor-helper-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.cursor-helper-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 700;
  color: #7986cb;
}

/* Billing explainer */
.cursor-billing-explainer {
  display: flex;
  align-items: stretch;
  gap: 0;
  background: rgba(255,255,255,0.04);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 8px;
}

.billing-explainer-item {
  flex: 1;
  min-width: 160px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.billing-explainer-sep {
  font-size: 1.4rem;
  font-weight: 700;
  color: #5c6bc0;
  display: flex;
  align-items: center;
  padding: 0 8px;
}

.explainer-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #7986cb;
}

.explainer-amount {
  font-size: 1.3rem;
  font-weight: 700;
  color: #e0e0e0;
}

.explainer-variable { color: #9fa8da; font-size: 1rem; }
.explainer-total-amt { color: #a5d6a7; }

.explainer-note {
  font-size: 0.75rem;
  color: #7986cb;
  line-height: 1.4;
}

.billing-explainer-total .explainer-label { color: #a5d6a7; }

/* CSV section */
.cursor-csv-section {
  background: rgba(255,255,255,0.04);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
}

.csv-section-title {
  font-size: 0.88rem;
  font-weight: 600;
  color: #c5cae9;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.csv-section-hint {
  font-size: 0.78rem;
  color: #7986cb;
  font-weight: 400;
}

.csv-section-hint a { color: #9fa8da; }

.csv-drop-zone {
  border: 2px dashed rgba(121, 134, 203, 0.5);
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  color: #7986cb;
  font-size: 0.88rem;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.csv-drop-zone:hover,
.csv-drop-zone--over {
  border-color: #7986cb;
  background: rgba(121, 134, 203, 0.1);
}

.csv-drop-zone i { font-size: 1.2rem; }

.csv-file-name {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #c5cae9;
}

/* CSV Preview */
.csv-preview {
  margin-top: 12px;
  border: 1px solid rgba(121, 134, 203, 0.3);
  border-radius: 8px;
  overflow: hidden;
}

.csv-preview-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  flex-wrap: wrap;
}

.csv-preview-row:last-child { border-bottom: none; }

.csv-preview-month {
  font-size: 0.9rem;
  font-weight: 600;
  color: #c5cae9;
  min-width: 110px;
}

.csv-preview-amounts {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  font-size: 0.82rem;
}

.csv-sub     { color: #9fa8da; }
.csv-included { color: #7986cb; }
.csv-ondemand { color: #ef9a9a; font-weight: 600; }
.csv-ondemand--zero { color: #4caf50; }
.csv-total   { color: #a5d6a7; margin-left: 4px; }

.csv-preview-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px 14px;
  background: rgba(255,255,255,0.03);
  border-top: 1px solid rgba(255,255,255,0.06);
}

.cursor-quick-btns {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.cursor-quick-btn {
  flex: 1;
  min-width: 220px;
  background: rgba(121, 134, 203, 0.15);
  border: 1px solid rgba(121, 134, 203, 0.4);
  border-radius: 8px;
  padding: 14px 16px;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
}

.cursor-quick-btn:hover {
  background: rgba(121, 134, 203, 0.28);
}

.quick-btn-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #c5cae9;
  margin-bottom: 4px;
}

.quick-btn-label i { color: #7986cb; }

.quick-btn-sub {
  font-size: 0.78rem;
  color: #7986cb;
  line-height: 1.4;
}

.btn-link {
  background: none;
  border: none;
  color: #666;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0 4px;
}
.btn-link:hover { color: #eee; }

.alert-close {
  margin-left: auto;
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: inherit;
  opacity: 0.6;
}

.alert-close:hover { opacity: 1; }

/* ── Loading ── */
.loading-state {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 1rem;
}

/* ── Reimburse Banner ── */
.reimburse-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #e8f5e9;
  border: 2px solid #2e7d32;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.banner-label {
  font-weight: 700;
  font-size: 1rem;
  color: #1b5e20;
  display: flex;
  align-items: center;
  gap: 8px;
}

.banner-sub { font-size: 0.85rem; color: #388e3c; margin-top: 4px; }

.banner-amount {
  font-size: 2rem;
  font-weight: 800;
  color: #1b5e20;
}

.reimbursed-banner {
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-radius: 10px;
  padding: 14px 20px;
  margin-bottom: 24px;
  color: #2e7d32;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
}

/* ── Summary Cards ── */
.summary-cards {
  display: flex;
  gap: 16px;
  margin-bottom: 28px;
  flex-wrap: wrap;
}

.summary-card {
  flex: 1;
  min-width: 140px;
  background: #f8f9ff;
  border: 1px solid #e0e4f0;
  border-radius: 10px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
}

.summary-card--total {
  background: #e8f5e9;
  border-color: #a5d6a7;
}

.summary-card .card-icon {
  font-size: 1.6rem;
  color: #2c3e8c;
  width: 40px;
  text-align: center;
}

.summary-card--total .card-icon { color: #2e7d32; }

.card-value {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a2e;
}

.summary-card--total .card-value { color: #1b5e20; }

.card-label {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
  margin-top: 2px;
}

.card-sub { font-size: 0.78rem; color: #999; margin-top: 2px; }

/* ── Section ── */
.table-section { margin-bottom: 32px; }

.section-header {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 10px;
}

.section-title {
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #2c3e8c;
  padding-bottom: 4px;
  border-bottom: 2px solid #2c3e8c;
}

.item-count {
  font-size: 0.8rem;
  color: #888;
  margin-left: 4px;
}

/* ── Expense Table ── */
.expense-table-wrap {
  border: 1px solid #e0e4f0;
  border-radius: 10px;
  overflow: hidden;
}

.expense-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.expense-table thead tr { background: #2c3e8c; color: #fff; }
.expense-table thead th {
  padding: 10px 12px;
  text-align: left;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.expense-table thead th.right { text-align: right; }
.expense-table thead th.center { text-align: center; }

.expense-table tbody tr { border-bottom: 1px solid #eef0f5; transition: background 0.1s; }
.expense-table tbody tr:nth-child(even) { background: #fafbff; }
.expense-table tbody tr:hover { background: #f0f2ff; }
.expense-table tbody tr.row-reimbursed { opacity: 0.65; }

.expense-table tbody td { padding: 9px 12px; vertical-align: top; }
.expense-table tbody td.right { text-align: right; }
.expense-table tbody td.center { text-align: center; }
.expense-table tbody td.amount-cell { font-weight: 600; white-space: nowrap; }

.desc-cell { max-width: 320px; }
.exp-notes { display: block; font-size: 0.78rem; color: #888; margin-top: 2px; }

.recurring-badge {
  margin-left: 6px;
  font-size: 0.7rem;
  color: #6c7a9c;
}

/* Category badges */
.cat-badge {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
}
.cat-INFRASTRUCTURE { background: #e3f2fd; color: #1565c0; }
.cat-AI_TOOLS       { background: #e8eaf6; color: #3949ab; }
.cat-DEV_TOOLS      { background: #e8f5e9; color: #2e7d32; }
.cat-SAAS           { background: #fff8e1; color: #f57f17; }
.cat-OTHER          { background: #f3e5f5; color: #6a1b9a; }

/* Source badge */
.source-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 0.75rem;
  background: #f0f0f0;
  color: #666;
  white-space: nowrap;
}

/* Status badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}
.status-paid    { background: #e8f5e9; color: #2e7d32; }
.status-pending { background: #fff3e0; color: #e65100; }

/* Action buttons */
.actions-cell { white-space: nowrap; }

.action-btn {
  background: none;
  border: 1px solid transparent;
  border-radius: 5px;
  padding: 4px 7px;
  cursor: pointer;
  font-size: 0.8rem;
  margin: 0 2px;
  transition: all 0.15s;
}
.action-btn--reimburse { color: #2e7d32; }
.action-btn--reimburse:hover { background: #e8f5e9; border-color: #a5d6a7; }
.action-btn--undo  { color: #888; }
.action-btn--undo:hover { background: #f0f0f0; }
.action-btn--edit  { color: #2c3e8c; }
.action-btn--edit:hover { background: #e8eaf6; border-color: #9fa8da; }
.action-btn--delete { color: #c62828; }
.action-btn--delete:hover { background: #fce4ec; border-color: #ef9a9a; }

/* Totals footer */
.expense-table tfoot tr.totals-row {
  background: #f0f2ff;
  border-top: 2px solid #c5cae9;
}
.expense-table tfoot td { padding: 10px 12px; }

/* Empty state */
.empty-state {
  text-align: center;
  padding: 48px 24px;
  color: #999;
}
.empty-state i { font-size: 2.5rem; margin-bottom: 12px; color: #dde2f0; }
.empty-state p { margin-bottom: 8px; }
.empty-hint { font-size: 0.85rem; }

/* ── YTD Table ── */
.ytd-section { margin-bottom: 32px; }

.ytd-table-wrap {
  border: 1px solid #e0e4f0;
  border-radius: 10px;
  overflow: hidden;
}

.ytd-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.ytd-table thead tr { background: #f0f2ff; }
.ytd-table thead th {
  padding: 9px 12px;
  text-align: left;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #2c3e8c;
}
.ytd-table thead th.right { text-align: right; }
.ytd-table tbody tr { border-bottom: 1px solid #eef0f5; }
.ytd-table tbody tr:hover { background: #f8f9ff; }
.ytd-table tbody td { padding: 9px 12px; }
.ytd-table tbody td.right { text-align: right; }

.ytd-current { background: #fffde7 !important; font-weight: 600; }

.current-label { font-size: 0.75rem; color: #f57f17; margin-left: 6px; }

.ytd-table tfoot tr { background: #2c3e8c; color: #fff; font-weight: 700; }
.ytd-table tfoot td { padding: 10px 12px; text-align: right; }
.ytd-table tfoot td:first-child { text-align: left; }

/* ── Buttons ── */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 7px;
  border: 1px solid transparent;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.btn:disabled { opacity: 0.5; cursor: default; }

.btn-primary   { background: #2c3e8c; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #3949ab; }

.btn-secondary { background: #fff; color: #2c3e8c; border-color: #c5cae9; }
.btn-secondary:hover:not(:disabled) { background: #e8eaf6; }

.btn-success   { background: #2e7d32; color: #fff; }
.btn-success:hover:not(:disabled) { background: #388e3c; }

.btn-sm { padding: 6px 12px; font-size: 0.82rem; }

/* ── Modal ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
}

.modal--sm { max-width: 420px; }

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #eef0f5;
}

.modal-header h3 { font-size: 1.1rem; font-weight: 700; color: #1a1a2e; }

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  color: #888;
}
.modal-close:hover { color: #333; }

.modal-body { padding: 20px 24px; }
.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #eef0f5;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* ── Forms ── */
.form-row { display: flex; gap: 16px; }
.form-row .form-group { flex: 1; }

.form-group { margin-bottom: 16px; }
.form-group label { display: block; font-size: 0.83rem; font-weight: 600; color: #444; margin-bottom: 5px; }
.form-group label .required { color: #c62828; }

.form-input {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #dde2f0;
  border-radius: 7px;
  font-size: 0.9rem;
  color: #1a1a2e;
  background: #fafbff;
  transition: border 0.15s;
}
.form-input:focus { outline: none; border-color: #2c3e8c; background: #fff; }
.form-input--lg { font-size: 1.2rem; font-weight: 600; text-align: right; }

.form-hint { font-size: 0.78rem; color: #888; margin-top: 4px; display: block; }

.form-group--checkbox { display: flex; align-items: center; padding-top: 24px; }

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.88rem;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.reimburse-detail {
  font-size: 0.9rem;
  color: #444;
  background: #f8f9ff;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 16px;
}

/* ── Misc ── */
.paid-text    { color: #2e7d32; font-weight: 600; }
.pending-text { color: #e65100; font-weight: 600; }
.muted        { color: #bbb; }
</style>
